/**
 * Central photo registry.
 *
 * Real job photography supplied by the business, pre-processed into
 * responsive WebP (with a JPEG fallback) at build-drop time and stored under
 * /public/photos. Each entry describes one photo: its aspect ratio (used to
 * reserve layout space and prevent CLS), a WebP srcset for modern browsers,
 * and a JPEG fallback for the rest.
 *
 * Components consume these via <ImageFrame photo={photo("...")} />. To add a
 * new photo, generate the sized derivatives, drop them in /public/photos with
 * the same "-640 / -1024 / -1600" naming, and add an entry here.
 */
export interface ResponsiveImage {
  /** WebP candidates for <source srcset>. */
  webpSrcSet: string;
  /** JPEG fallback used as <img src>. */
  fallback: string;
  /** Intrinsic aspect ratio (width / height). */
  aspect: number;
  /** Intrinsic dimensions of the fallback image, for width/height hints. */
  width: number;
  height: number;
}

export type PhotoKey =
  | "core-drilling"
  | "concrete-core"
  | "slab-cut-line"
  | "wall-opening"
  | "concrete-removal"
  | "saw-cutting"
  | "commercial-site"
  | "road-sawing";

const WIDTHS = [640, 1024, 1600] as const;

// aspect = width / height of each source photo.
const ASPECTS: Record<PhotoKey, number> = {
  "core-drilling": 0.75,
  "concrete-core": 0.75,
  "slab-cut-line": 0.75,
  "wall-opening": 1.3333,
  "concrete-removal": 1.3333,
  "saw-cutting": 0.75,
  "commercial-site": 0.75,
  "road-sawing": 1.3298,
};

/** Build the responsive descriptor for a known photo key. */
export function photo(key: PhotoKey): ResponsiveImage {
  const aspect = ASPECTS[key];
  const webpSrcSet = WIDTHS.map((w) => `/photos/${key}-${w}.webp ${w}w`).join(", ");
  const fallbackWidth = 1024;
  return {
    webpSrcSet,
    fallback: `/photos/${key}-1024.jpg`,
    aspect,
    width: fallbackWidth,
    height: Math.round(fallbackWidth / aspect),
  };
}
